import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useQuery } from "react-query";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { show_toast } from "utils/helpers";
import { issue_motor_cert } from "services/apiSrv";
import { getDmvicConfig } from "services/dvmvic";

// Validation Schema
const validationSchema = Yup.object().shape({
  issuanceType: Yup.string().required("Issuance type is required"),
  thirdPartyIssuer: Yup.string().when("issuanceType", {
    is: "auto",
    then: Yup.string().required("Third party issuer is required"),
    otherwise: Yup.string().notRequired(),
  }),
  validateDoubleInsurance: Yup.string().when("thirdPartyIssuer", {
    is: "dmvic",
    then: Yup.string().required("Validate double insurance option is required"),
    otherwise: Yup.string().notRequired(),
  }),
  dmvicCategory: Yup.string().when("thirdPartyIssuer", {
    is: "dmvic",
    then: Yup.string().required("DMVIC category is required"),
    otherwise: Yup.string().notRequired(),
  }),
  dmvicVehicleType: Yup.string().when("thirdPartyIssuer", {
    is: "dmvic",
    then: Yup.string().required("DMVIC vehicle type is required"),
    otherwise: Yup.string().notRequired(),
  }),
  onDmvicFailure: Yup.string().when("thirdPartyIssuer", {
    is: "dmvic",
    then: Yup.string().required("Select action on DMVIC failure"),
    otherwise: Yup.string().notRequired(),
  }),
  dmvicCertificate: Yup.string().when("thirdPartyIssuer", {
    is: "dmvic",
    then: Yup.string().required("DMVIC certificate option is required"),
    otherwise: Yup.string().notRequired(),
  }),

  certificateIssuing: Yup.string().when("thirdPartyIssuer", {
    is: "dmvic",
    then: Yup.string().required("Certificate issuing status is required"),
    otherwise: Yup.string().notRequired(),
  }),
});

// Options
const issuanceOptions = [
  { value: "auto", label: "Auto" },
  { value: "manual", label: "Manual" },
];
const thirdPartyOptions = [
  { value: "dmvic", label: "DMVIC" },
  { value: "Others", label: "Others" },
  { value: "unknown", label: "Unknown" },
];
const onFailureOptions = [
  { value: "auto-refund", label: "Auto Refund" },
  { value: "manual-refund", label: "Manual Refund" },
  { value: "raiseException", label: "Raise Exception" },
];
const certificateOptions = [
  { value: "sendClient", label: "Send to Client" },
  { value: "sendUs", label: "Send to Intermediary" },
  { value: "sendBoth", label: "Send to Client and Intermediary" },
];
const dmvicCategories = [
  { value: "1", label: "Class A PSV Unmarked" },
  { value: "8", label: "Type A Taxi" },
  { value: "4", label: "Type D Motorcycle" },
  { value: "9", label: "Type D PSV Motorcycle" },
  { value: "10", label: "Type D Motorcycle Commercial" },
];
const dmvicVehicleTypes = [
  { value: "1", label: "Own Goods" },
  { value: "2", label: "General Cartage" },
  { value: "3", label: "Institutional" },
  { value: "4", label: "Special" },
  { value: "5", label: "Tankers" },
  { value: "6", label: "Motor Trade" },
];
const validateOptions = [
  { value: "prepayment", label: "Collect Payment before DMVIC verification" },
  { value: "postpayment", label: "Collect Payment after DMVIC verification" },
  { value: "ignore", label: "Ignore" },
];
const certificateIssuingOptions = [
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

export default function MotorCertificateForm({refetchProductDetails}) {
  const { id: productSystemRef } = useParams();
  const initialValues = {
    issuanceType: "",
    thirdPartyIssuer: "",
    validateDoubleInsurance: "",
    dmvicCategory: "",
    dmvicVehicleType: "",
    onDmvicFailure: "",
    dmvicCertificate: "",
    intergrationConfigRef: "",
    certificateIssuing: "",
  };


  // ────────────────────────────────────────────────
  //                DMVIC Configuration
  // ────────────────────────────────────────────────
  const {
    data: dmvicConfig = [],
    isLoading: loadingDmvic,
    refetch: refetchDmvic,
  } = useQuery({
    queryKey: ["dmvic-config"],
    queryFn: async () => {
      const res = await getDmvicConfig();
      return res.data || null;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });

  console.log("DMVICCONFIG", dmvicConfig)

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await issue_motor_cert({ ...values, productSystemRef, intergrationConfigRef: dmvicConfig?.intermediaryDmvicConfigSystemRef });
      show_toast("Motor certificate saved successfully!", "success");
      refetchProductDetails?.()
      resetForm();
    } catch (error) {
      show_toast("Failed to save motor certificate", "error");
      console.error("Submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-sm my-4">
      <h1 className="mb-4">Motor certificate issuance</h1>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, isSubmitting, resetForm }) => (
          <Form className="mb-3">
            <div className="row g-3">
              {/* Issuance Type */}
              <div className="col-12">
                <label className="form-label fw-semibold">Issuance Type</label>
                <div>
                  {issuanceOptions.map(({ value, label }) => (
                    <div className="form-check form-check-inline" key={value}>
                      <Field
                        type="radio"
                        name="issuanceType"
                        value={value}
                        className="form-check-input"
                        id={`issuanceType-${value}`}
                        aria-label={`Issuance type: ${label}`}
                      />
                      <label htmlFor={`issuanceType-${value}`} className="form-check-label">
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
                <ErrorMessage name="issuanceType" component="div" className="invalid-feedback d-block" />
              </div>

              {/* Third Party Issuer (if auto) */}
              {values.issuanceType === "auto" && (
                <div className="col-12">
                  <label className="form-label fw-semibold">Third Party Issuer</label>
                  <div>
                    {thirdPartyOptions.map(({ value, label }) => (
                      <div className="form-check form-check-inline" key={value}>
                        <Field
                          type="radio"
                          name="thirdPartyIssuer"
                          value={value}
                          className="form-check-input"
                          id={`thirdPartyIssuer-${value}`}
                          aria-label={`Third party issuer: ${label}`}
                        />
                        <label htmlFor={`thirdPartyIssuer-${value}`} className="form-check-label">
                          {label}
                        </label>
                      </div>
                    ))}
                  </div>
                  <ErrorMessage name="thirdPartyIssuer" component="div" className="invalid-feedback d-block" />
                </div>
              )}

              {/* DMVIC Settings (if issuanceType === "auto" and thirdPartyIssuer === "DMVIC") */}
              {values.issuanceType === "auto" && values.thirdPartyIssuer === "dmvic" && (
                <>
                  <div className="col-12">
                    <fieldset className="border rounded p-3">
                      <legend className="float-none w-auto px-2 fw-semibold fs-6">DMVIC Settings</legend>

                      {/* Validate Double Insurance */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Validate Double Insurance</label>
                        <div>
                          {validateOptions.map(({ value, label }) => (
                            <div className="form-check form-check-inline" key={value}>
                              <Field
                                type="radio"
                                name="validateDoubleInsurance"
                                value={value}
                                className="form-check-input"
                                id={`validateDoubleInsurance-${value}`}
                                aria-label={`Validate double insurance: ${label}`}
                              />
                              <label htmlFor={`validateDoubleInsurance-${value}`} className="form-check-label">
                                {label}
                              </label>
                            </div>
                          ))}
                        </div>
                        <ErrorMessage
                          name="validateDoubleInsurance"
                          component="div"
                          className="invalid-feedback d-block"
                        />
                      </div>

                      {/* DMVIC Category and Vehicle Type */}
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label htmlFor="dmvicCategory" className="form-label fw-semibold">
                            DMVIC Category
                          </label>
                          <Field as="select" name="dmvicCategory" id="dmvicCategory" className="form-select form-control">
                            <option value="">Select category</option>
                            {dmvicCategories.map(({ value, label }) => (
                              <option value={value} key={value}>
                                {label}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage name="dmvicCategory" component="div" className="invalid-feedback d-block" />
                        </div>

                        <div className="col-md-6">
                          <label htmlFor="dmvicVehicleType" className="form-label fw-semibold">
                            DMVIC Vehicle Type
                          </label>
                          <Field as="select" name="dmvicVehicleType" id="dmvicVehicleType" className="form-select form-control">
                            <option value="">Select vehicle type</option>
                            {dmvicVehicleTypes.map(({ value, label }) => (
                              <option value={value} key={value}>
                                {label}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage name="dmvicVehicleType" component="div" className="invalid-feedback d-block" />
                        </div>
                      </div>
                    </fieldset>
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">On DMVIC Certificate Failure</label>
                    <div>
                      {onFailureOptions.map(({ value, label }) => (
                        <div className="form-check form-check-inline" key={value}>
                          <Field
                            type="radio"
                            name="onDmvicFailure"
                            value={value}
                            className="form-check-input"
                            id={`onDmvicFailure-${value}`}
                            aria-label={`On DMVIC failure: ${label}`}
                          />
                          <label htmlFor={`onDmvicFailure-${value}`} className="form-check-label">
                            {label}
                          </label>
                        </div>
                      ))}
                    </div>
                    <ErrorMessage name="onDmvicFailure" component="div" className="invalid-feedback d-block" />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">DMVIC Certificate</label>
                    <div>
                      {certificateOptions.map(({ value, label }) => (
                        <div className="form-check form-check-inline" key={value}>
                          <Field
                            type="radio"
                            name="dmvicCertificate"
                            value={value}
                            className="form-check-input"
                            id={`dmvicCertificate-${value}`}
                            aria-label={`DMVIC certificate: ${label}`}
                          />
                          <label htmlFor={`dmvicCertificate-${value}`} className="form-check-label">
                            {label}
                          </label>
                        </div>
                      ))}
                    </div>
                    <ErrorMessage name="dmvicCertificate" component="div" className="invalid-feedback d-block" />
                  </div>
                  <div className="col-12">
                      <label className="form-label fw-semibold">
                        Certificate Issuing
                      </label>
                      <div>
                        {certificateIssuingOptions.map(({ value, label }) => (
                          <div
                            className="form-check form-check-inline"
                            key={value}
                          >
                            <Field
                              type="radio"
                              name="certificateIssuing"
                              value={value}
                              className="form-check-input"
                              id={`certificateIssuing-${value}`}
                              aria-label={`Certificate issuing: ${label}`}
                            />
                            <label
                              htmlFor={`certificateIssuing-${value}`}
                              className="form-check-label"
                            >
                              {label}
                            </label>
                          </div>
                        ))}
                      </div>
                      <ErrorMessage
                        name="certificateIssuing"
                        component="div"
                        className="invalid-feedback d-block"
                      />
                    </div>
                </>
              )}
            </div>

            <div className="d-flex justify-content-start mt-4">
              <button type="submit" className="btn btn-primary px-4 py-2 me-2" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
              <button type="button" className="btn btn-secondary px-4 py-2" onClick={() => resetForm()} disabled={isSubmitting}>
                Reset
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}